import { findLeadMessagePhones } from '@app/components/LeadCard/ui/MessageHistory/lib/findLeadMessagePhones';
import { WHATSAPP } from '@app/constants/socialIntegrationsTypes';
import type { NDictionaries } from '@app/types';

/** Часть текста сообщения: обычный текст, жирный фрагмент или номер. */
export type LeadMessagePart =
  | { kind: 'text'; value: string; isBold: boolean }
  | { kind: 'phone'; value: string; digits: string; countryId: number };

// Тот же паттерн, что в formatLeadMessageText: WhatsApp помечает жирное
// звёздочками. Ту функцию не трогаем — её используют ещё шесть компонентов.
const WHATSAPP_BOLD_PATTERN = /\*(.*?)\*/g;

interface BoldSegment {
  value: string;
  isBold: boolean;
}

const splitByBold = (
  text: string,
  socialType: string | null
): BoldSegment[] => {
  if (socialType !== WHATSAPP) return [{ value: text, isBold: false }];

  const segments: BoldSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(WHATSAPP_BOLD_PATTERN)) {
    if (match.index === undefined) continue;

    if (match.index > lastIndex) {
      segments.push({
        value: text.slice(lastIndex, match.index),
        isBold: false,
      });
    }

    segments.push({ value: match[1], isBold: true });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ value: text.slice(lastIndex), isBold: false });
  }

  return segments;
};

const splitByPhones = (
  segment: BoldSegment,
  countries: NDictionaries.ModelCountry[]
): LeadMessagePart[] => {
  const phones = findLeadMessagePhones(segment.value, countries);

  if (!phones.length) {
    return [{ kind: 'text', value: segment.value, isBold: segment.isBold }];
  }

  const parts: LeadMessagePart[] = [];
  let cursor = 0;

  phones.forEach(({ start, end, raw, digits, countryId }) => {
    if (start > cursor) {
      parts.push({
        kind: 'text',
        value: segment.value.slice(cursor, start),
        isBold: segment.isBold,
      });
    }

    parts.push({ kind: 'phone', value: raw, digits, countryId });
    cursor = end;
  });

  if (cursor < segment.value.length) {
    parts.push({
      kind: 'text',
      value: segment.value.slice(cursor),
      isBold: segment.isBold,
    });
  }

  return parts;
};

/**
 * Разбирает текст сообщения на части для рендера React-узлами: жирные
 * фрагменты WhatsApp и найденные номера телефонов.
 */
export const parseLeadMessageParts = (
  text: string,
  socialType: string | null,
  countries: NDictionaries.ModelCountry[]
): LeadMessagePart[] =>
  splitByBold(text, socialType).flatMap((segment) =>
    splitByPhones(segment, countries)
  );
